import { debounce } from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  ReactSelectInput,
  RadiosInput,
  TextInput,
} from '@hivebrite/design-system/Inputs';
import { Loader } from '@hivebrite/design-system/Loader';

import SuccessCircleCheck from 'hb-react/shared/icons/SuccessCircleCheck';

import Checkbox from '../Checkbox';
import { CKEditorTextarea, Textarea } from '../Input';

const FIELD_AUTOSAVE_STATES = {
  idle: 'idle',
  saving: 'saving',
  saved: 'saved',
};

const Saving = ({ state }) => {
  const theme = useTheme();

  if (state === FIELD_AUTOSAVE_STATES.saving) {
    return (
      <Box>
        <Box fontWeight={2} alignSelf="center" color={theme.colors.asphalt}>
          {__('Saving...')}
        </Box>
        <Box ml={2}>
          <Loader size={18} />
        </Box>
      </Box>
    );
  }

  if (state === FIELD_AUTOSAVE_STATES.saved) {
    return (
      <Box justifyContent="flex-center">
        <Box fontWeight={2} alignSelf="center" color={theme.colors.grass}>
          {__('Successfully Saved')}
        </Box>
        <Box ml={2}>
          <SuccessCircleCheck width={18} />
        </Box>
      </Box>
    );
  }

  return null;
};

const FieldAutoSave = ({
  children,
  onSave,
  debounce: debounceDelay = 1000,
  ...otherProps
}) => {
  const [state, setState] = useState(FIELD_AUTOSAVE_STATES.idle);

  const onSaveDebounced = useCallback(
    debounce(async (value) => {
      setState(FIELD_AUTOSAVE_STATES.saving);
      await onSave(value);
      setState(FIELD_AUTOSAVE_STATES.saved);
    }, debounceDelay),
    [],
  );

  useEffect(() => {
    if (FIELD_AUTOSAVE_STATES.saved) {
      const timeout = setTimeout(
        () => setState(FIELD_AUTOSAVE_STATES.idle),
        2000,
      );

      return () => clearTimeout(timeout);
    }
  }, [state]);

  return (
    <Box alignItems="flex-end" {...otherProps} position="relative">
      {children({ onSaveDebounced })}
      {state !== FIELD_AUTOSAVE_STATES.idle && (
        <Box
          height={20}
          ml={2}
          maxWidth={300}
          position="absolute"
          right={0}
          top={0}
        >
          <Saving state={state} />
        </Box>
      )}
    </Box>
  );
};

export default FieldAutoSave;

export const TextAutoSave = ({
  onSave,
  onChange,
  containerStyle,
  ...props
}) => (
  <FieldAutoSave onSave={onSave} {...containerStyle}>
    {({ onSaveDebounced }) => (
      <TextInput
        {...props}
        onChange={(event) => {
          onChange?.(event);
          onSaveDebounced(event.target.value);
        }}
      />
    )}
  </FieldAutoSave>
);

export const TextAreaAutoSave = ({
  onSave,
  onChange,
  containerStyle,
  ...props
}) => (
  <FieldAutoSave onSave={onSave} {...containerStyle}>
    {({ onSaveDebounced }) => (
      <Box flex={1}>
        <Textarea
          {...props}
          onChange={(event) => {
            onChange && onChange(event);
            onSaveDebounced(event.target.value);
          }}
        />
      </Box>
    )}
  </FieldAutoSave>
);

export const CheckboxAutoSave = ({
  onSave,
  onChange,
  containerStyle,
  ...props
}) => (
  <FieldAutoSave
    onSave={onSave}
    debounce={0}
    alignItems="center"
    {...containerStyle}
  >
    {({ onSaveDebounced }) => (
      <Checkbox
        {...props}
        onChange={(event) => {
          onChange && onChange(event);
          onSaveDebounced(event.target.checked);
        }}
      />
    )}
  </FieldAutoSave>
);

export const CKEditorTextAreaAutoSave = ({
  onSave,
  onChange,
  containerStyle,
  ...props
}) => (
  <FieldAutoSave onSave={onSave}>
    {({ onSaveDebounced }) => (
      <Box flex={1}>
        <CKEditorTextarea
          {...props}
          onChange={(event) => {
            onChange?.(event);
            onSaveDebounced(event.target.value);
          }}
        />
      </Box>
    )}
  </FieldAutoSave>
);

export const SelectAutoSave = ({
  onSave,
  onChange,
  containerStyle,
  ...props
}) => (
  <FieldAutoSave onSave={onSave} debounce={0} {...containerStyle}>
    {({ onSaveDebounced }) => (
      <ReactSelectInput
        {...props}
        onChange={(event) => {
          onChange?.(event);
          onSaveDebounced(event.target.value);
        }}
      />
    )}
  </FieldAutoSave>
);

export const RadiosAutoSave = ({
  onSave,
  onChange,
  containerStyle,
  ...props
}) => (
  <FieldAutoSave onSave={onSave} debounce={0} {...containerStyle}>
    {({ onSaveDebounced }) => (
      <RadiosInput
        {...props}
        onChange={(value) => {
          onChange?.(value);
          onSaveDebounced(value);
        }}
      />
    )}
  </FieldAutoSave>
);
